<template>
  <!-- PAGE: Detailed Inspection -->
  <div class="row mb-2">
    <div class="col">
      <div class="d-flex align-items-center">
        <router-link :to="routeTo">
          <button class="btn back-button p-1 me-2 me-md-4">
            <i class="fa-solid fa-chevron-left custom-arrow"></i>
          </button>
        </router-link>
        <h3 class="header-text mb-0 me-md-3">{{ firmName }}</h3>
      </div>
    </div>
    <div v-if="isAdmin" class="col-3">
      {{ providerName }}
    </div>
    <div class="col-2 d-flex justify-content-end align-items-start">
      <!-- #620: ADAS IQ pinned to this request (beside Save, which is only there with unsaved changes) -->
      <button v-if="hasAdasIq" class="btn btn-outline-primary btn-sm me-2 text-nowrap" :class="{ active: adasIqOpen }"
        data-test="adasiq-toggle" @click="adasIqOpen = !adasIqOpen">
        <i class="fa-solid fa-comments"></i> ADAS IQ
      </button>
      <button v-if="(isProvider || isAdmin) && isSave" class="btn btn-danger" :disabled="isDisabled || isCompleted"
        @click="saveOffer">Save</button>
    </div>
    <div class="col-2 text-right">
      <button v-if="isProvider || isAdmin" class="btn btn-primary btn-sm me-2" :disabled="isDisabled || isCompleted"
        @click="sendOffer">Send</button>
      <button class="btn btn-primary btn-sm" @click="submitIssue">Submit Issue</button>
    </div>
  </div>
  <!-- #620: ADAS IQ, pinned to this request's inspection and vehicle (ADAS IQ ADR-0018) -->
  <div v-if="hasAdasIq && adasIqOpen" class="row mb-3">
    <div class="col">
      <AdasIqEmbed :context="adasIqContext" height="max(360px, calc(100vh - 260px))" />
    </div>
  </div>
  <!-- CONTENT: Detailed Inspection -->
  <div class="row">
    <!-- LEFT COLUMN: Vehicle, RO Summary, Calibrations -->
    <div class="col-8">
      <!-- Vehicle and RO Summary -->
      <div class="row">
        <div class="col">
          <VehicleSummary :vin="vin" :status="status" :source="source" @report="report" @invoice="invoice" />
        </div>
      </div>
      <!-- Calibrations Header (with dates) -->
      <div class="row mt-2">
        <div class="col-4">
          <div class="custom-recommend">
            <span class="custom-label">Date Recommended:</span>
            <span class="ms-2">{{ inspectionDate }}</span>
          </div>
        </div>
        <div v-if="isInsurer" class="col">
          <span class="custom-label"># Calibrations:</span>
          <span class="custom-numbers"></span>
        </div>
        <div v-else class="col">
          <span class="custom-label"># Calibrations / # ADAS Found:</span>
          <span class="text-danger custom-numbers ms-3">{{ inspectionInfo?.numCalibration }}</span> /
          <span class="custom-numbers">{{ inspectionInfo?.numAdas }}</span>
        </div>
        <div v-if="!isInsurer" class="col-4 custom-status">
          <div class="row">
            <div class="col">
              <span class="custom-label">Status:</span>
              <span class="ms-2" :class="statusClass">{{ statusName }}</span>
              <div v-if="isReason" class="custom-reason">
                <button class="btn btn-link" @click="viewReason"><i class="fas fa-sticky-note"></i></button>
                {{ reasonName }}
              </div>
              <div v-else-if="isCompleted" class="custom-reason">
                <span>on {{ deliveryDate }}</span>
              </div>
            </div>
            <div class="col">
              <!-- Pending means "the shop has not picked a provider yet", so the control is normally
                   locked. A request the provider uploaded themselves has no such choice pending, and
                   would otherwise sit at Pending forever with nothing able to move it on. -->
              <select v-model="newStatus" :disabled="(isPending && !isProviderUpload) || isDisabled"
                class="form-control" @change="updateStatus">
                <option v-for="status in statuses" :key="status.id" :value="status.id">{{ status.name }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div v-if="(isProvider || isAdmin) && isScheduled" class="row">
        <div class="col-6 d-flex align-items-center">
          <div class="custom-scheduled-highlight">
            <span class="custom-label">Date Scheduled:</span>
            <!-- An unscheduled request used to show the label with nothing after it, which reads as a
                 rendering fault rather than as work still to be booked. -->
            <span class="ms-2" :class="{ 'custom-not-scheduled': !scheduledDate }">
              {{ scheduledDate || 'Not Scheduled' }}
            </span>
          </div>
          <button v-if="isProvider && canSchedule && !isDisabled && !isCompleted" class="btn btn-primary ms-2"
            @click="confirm">Confirm / Assign Technician</button>
        </div>
        <div class="col-6 custom-service">
          <span class="custom-label">Technician:</span>
          <span class="ms-2">{{ techs }}</span>

          <span v-if="isLocked" class="custom-discount">* amount reflects discounted price</span>
        </div>
      </div>
      <!-- #539: Pricing Source badge. Provider view only — an insurer looking at this
           screen has no pricing column, so the badge would describe nothing. -->
      <div v-if="!isInsurer && pricingSourceBadge" class="row mt-2">
        <div class="col-12 custom-pricing-source">
          <span class="custom-pricing-source-text">Pricing Source:</span>
          <span
            class="badge"
            :class="{
              'bg-primary': pricingSourceBadge.source === 'insurer',
              'bg-success': pricingSourceBadge.source === 'provider'
            }"
            :title="pricingSourceBadge.detail || ''"
          >{{ pricingSourceLabel(pricingSourceBadge.source) }}</span>
        </div>
      </div>
      <!-- #684: no vehicle make means make-specific pricing can't apply -->
      <div v-if="isMissingVehicleMake" class="row mt-2">
        <div class="col-12">
          <div class="alert alert-warning py-2 mb-0">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Vehicle make is missing. ADAS Map can't confirm which calibrations are required, and standard (non-make) pricing is shown.
          </div>
        </div>
      </div>
      <!-- Calibrations -->
      <div class="row mt-3 pr-2">
        <!-- TABLE VIEW: Calibrations -->
        <div class="col d-md-block">
          <!-- #675: Required vs Not Required / Not Equipped systems, kept apart -->
          <ZTabs :modelValue="calibrationTab" :tabs="calibrationTabs" @change="calibrationTab = $event" />
          <div class="overflow-auto table-holder">
            <template v-if="calibrationTab === 'Required'">
            <!-- For Insurers, List of Calibrations -->
            <ZTable v-if="isInsurer" v-model="calibrations" class="table table-bordered table-striped"
              :is-single-row-detail="false" :is-header-fixed="true" :columns="tableHeader">
              <template #tbody="props">
                <td>{{ props.row.category }}</td>
                <td>{{ props.row.location }}</td>
                <td>{{ props.row.system }}</td>
                <td>{{ props.row.calibration }}</td>
                <td class="custom-column-60-center">
                  <a v-popover tab-index="0" data-bs-toggle="popover" data-bs-trigger="focus hover" data-bs-html="true"
                    data-bs-sanitize="false" :title="props.row.calibration"
                    :data-bs-content="prepTrigger(props.row.triggerNotice)" class="btn btn-link" role="button">
                    <i class="fa-solid fa-location-crosshairs"></i>
                  </a>
                </td>
                <td></td>
              </template>
              <template #empty> Nothing found! </template>
            </ZTable>
            <!-- For Providers, List of Calibrations, allowing for price changes -->
            <table v-else class="table table-bordered table-striped">
              <thead class="bg-white table-sticky">
                <tr>
                  <th v-for="column in tableColumnsName" :key="column.field" :class="column.class">
                    <span>{{ column.label }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, index) in calibrations" :key="index">
                  <td>{{ line.id }}</td>
                  <td>{{ line.category }}</td>
                  <td>{{ line.location }}</td>
                  <td>{{ line.system }}
                    <a v-if="line.isUserInstalled" v-popover tab-index="0" data-bs-toggle="popover"
                      data-bs-trigger="focus hover" data-bs-html="true" data-bs-sanitize="false"
                      title="User Added System"
                      data-bs-content="This system/component was manually identified by the user as being installed and was not detected through standard VIN decoding. The component was subsequently validated against the ADAS Map database and user-provided repair lines to determine calibration and/or inspection applicability."
                      class="btn btn-link" role="button">
                      <i class="fa-solid fa-user-plus ms-2 text-success"></i>
                    </a>
                  </td>
                  <td>{{ line.calibration }}</td>
                  <td class="text-right">{{ formatNumber(line.amount) }}</td>
                  <td v-if="isLocked" class="text-right">{{ formatNumber(line.discount) }}</td>
                  <td v-if="!isLocked" class="custom-offer-price">
                    <input :value="line.offerPrice" type="number" min="0" max="99999" class="form-control text-right"
                      :disabled="isDisabled || isCompleted" @keyup="setOfferPrice(line.id, $event.target.value)"
                      @change="setOfferPrice(line.id, $event.target.value)" />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" class="text-right custom-opportunity">Total $:</td>
                  <td class="text-right custom-opportunity">{{ formatNumber(inspectionInfo?.price) }}</td>
                  <td v-if="isLocked" class="text-right custom-opportunity">{{ formatNumber(inspectionInfo?.discount) }}
                  </td>
                  <td v-if="!isLocked" class="text-right custom-opportunity">{{ formatNumber(inspectionInfo?.offerPrice)
                  }}</td>
                </tr>
                <tr>
                  <td colspan="5" class="text-right custom-opportunity">Referral Fee $:</td>
                  <td class="text-right custom-opportunity">{{ formatNumber(inspectionInfo?.revenueShareFee) }}</td>
                  <!-- <td v-else class="text-right custom-opportunity"></td>
                  <td v-if="!isLocked" class="text-right custom-opportunity">{{ formatNumber(inspectionInfo?.revenueShareFee) }}</td> -->
                </tr>
              </tfoot>
            </table>
            </template>
            <!-- Not Required / Not Equipped: identified on the vehicle, not recommended as a service -->
            <ZTable v-else v-model="notRequiredCalibrations" class="table table-bordered table-striped"
              :is-single-row-detail="false" :is-header-fixed="true" :columns="notRequiredHeader">
              <template #tbody="props">
                <td>{{ props.row.category }}</td>
                <td>{{ props.row.location }}</td>
                <td>{{ props.row.system }}</td>
                <td>{{ props.row.calibration }}</td>
                <td class="custom-column-60-center">
                  <a v-if="props.row.triggerNotice" v-popover tab-index="0" data-bs-toggle="popover"
                    data-bs-trigger="focus hover" data-bs-html="true" data-bs-sanitize="false"
                    :title="props.row.calibration" :data-bs-content="prepTrigger(props.row.triggerNotice)"
                    class="btn btn-link" role="button">
                    <i class="fa-solid fa-location-crosshairs"></i>
                  </a>
                </td>
              </template>
              <template #empty> Nothing found! </template>
            </ZTable>
            <span>id: {{ opportunityId }}</span>
          </div>
        </div>
        <!-- CARD VIEW (NOT USED): Calibrations -->
        <div class="col d-block d-md-none">
          <div class="shop-card">
            <div class="row m-0 dashboard-shop-card">
              <div v-if="isLoading" class="col-12 col-sm-12 shop-list-container p-2">
                <Loader singleCard="true" />
              </div>
              <div v-if="calibrations?.length === 0 && !isLoading" class="col-12 col-sm-12 shop-list-container p-2">
                <Loader singleEmptyCard="true" />
              </div>
              <div v-if="calibrations && !isLoading">
                <div v-for="(item, idx) of calibrations" :key="idx" class="col-12 col-sm-12 shop-list-container p-2">
                  <div class="shop-list-div p-3">
                    <div class="shop-header-mobile pb-3 d-flex justify-content-between align-items-center">
                      <div>
                        <p class="shop-name-mobile colorPrimary text-start fw-6 fs-16 m-0">
                          {{ item.calibration }}
                        </p>
                      </div>
                      <div>
                        <a v-popover tab-index="0" data-bs-toggle="popover" data-bs-trigger="focus hover"
                          data-bs-html="true" data-bs-sanitize="false" :title="item.calibration"
                          :data-bs-content="prepTrigger(item.triggerNotice)" class="btn btn-link" role="button">
                          <i class="fa-solid fa-location-crosshairs"></i>
                        </a>
                      </div>
                    </div>
                    <div class="shop-body-mobile d-flex justify-content-between pt-3">
                      <div>
                        <p class="text-start colorPrimary fs-12 m-0">Category</p>
                        <p class="shop-service-required-mobile fs-14 colorPrimary text-start fw-b m-0">
                          {{ item.category }}
                        </p>
                      </div>
                      <div>
                        <p class="text-start fs-12 colorPrimary m-0">location</p>
                        <p class="shop-service-completed-mobile text-start fs-14 colorPrimary fw-b m-0">
                          {{ item.location }}
                        </p>
                      </div>
                    </div>
                    <div class="shop-body-mobile d-flex justify-content-between pt-3">
                      <div>
                        <p class="text-start colorPrimary fs-12 m-0">System</p>
                        <p class="shop-service-required-mobile fs-14 colorPrimary text-start fw-b m-0">
                          {{ item.system }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- RIGHT COLUMN: Scans and Documents -->
    <div class="col-4">
      <div class="accordion accordion-flush" id="accordionDetails">
        <div v-if="!isInsurer" class="accordion-item">
          <div class="accordion-header" id="headingNotes">
            <button class="accordion-button collapsed custom-accordion-button" type="button" data-bs-toggle="collapse"
              data-bs-target="#collapseNotes" aria-expanded="false" aria-controls="collapseNotes">
              Notes
              <span class="custom-note-count"># {{ requestNoteCount }}</span>
            </button>
          </div>
          <div id="collapseNotes" class="accordion-collapse collapse" aria-labelledby="headingNotes"
            data-parent="#accordionDetails">
            <div class="accordion-body">
              <RequestNotes />
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <div class="accordion-header" id="headingScans">
            <button class="accordion-button collapsed custom-accordion-button" type="button" data-bs-toggle="collapse"
              data-bs-target="#collapseScans" aria-expanded="false" aria-controls="collapseScans">
              Scans
              <span class="custom-note-count"># {{ scansCount }}</span>
            </button>
          </div>
          <div id="collapseScans" class="accordion-collapse collapse" aria-labelledby="headingScans"
            data-parent="#accordionDetails">
            <div class="accordion-body">
              <div class="row">
                <!-- Scans -->
                <div class="col">
                  <AppDocuments :vin="vin" :refId="opportunityId" ref="scans" name="scanned-documents"
                    folder="clientscans" title="Scanning" type="scan" :isNameUpdate="false" :isShowScans="true"
                    :isRepairNetwork="isRepairNetwork" nothingFound="no scans found" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <div class="accordion-header" id="headingDocs">
            <button class="accordion-button collapsed custom-accordion-button" type="button" data-bs-toggle="collapse"
              data-bs-target="#collapseDocs" aria-expanded="false" aria-controls="collapseDocs">
              Documentation
              <span class="custom-note-count"># {{ docCount }}</span>
            </button>
          </div>
          <div id="collapseDocs" class="accordion-collapse collapse" aria-labelledby="headingDocs"
            data-parent="#accordionDetails">
            <div class="accordion-body">
              <div class="row">
                <!-- Documentation -->
                <div class="col">
                  <AppDocuments :vin="vin" :refId="opportunityId" ref="documents" name="documents-evidence"
                    folder="clientfiles" title="Documentation" type="file" :isShowScans="false"
                    :isRepairNetwork="isRepairNetwork" nothingFound="no documents found" :isUpload="true"
                    :isNameUpdate="true" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <DialogPreview :show="actionPreview" modalSize="modal-xl">
    <template v-slot:header>
      <div class="row" style="width: 100%">
        <div class="col">
          <span class="custom-title">Preview</span>
          <span class="float-end" data-bs-dismiss="modal" @click="closeModal">
            <i class="fa fa-close" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </template>
    <template v-slot:body>
      <div v-if="isLoading">
        <span>loading...</span>
      </div>
      <div v-else-if="isError" class="custom-error">
        <div class="row">
          <div class="col">
            <span>{{ errorMessage }}</span>
          </div>
        </div>
      </div>
      <object v-else width="100%" :height="height" :data="previewContent" :type="mimeType"></object>
    </template>
    <template v-slot:footer>
      <button type="button" class="secondaryButton px-4" data-bs-dismiss="modal" @click="closeModal">Okay</button>
    </template>
  </DialogPreview>
  <DialogSchedule v-model="actionSchedule" @close="closeModal" @assigned="assignedTech" @saved="savedSchedule" />
  <DialogConfirm v-model="actionConfirm" :confirmTitle="confirmTitle" :confirmAction="confirmAction"
    :confirmQuestion="confirmQuestion" :isChoice="isChoice" :isText="isText" :defaultText="defaultText"
    :choiceLabel="choiceLabel" :choices="choices" @closing="closeModal" @proceed="performConfirmation" />
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { lookupStore } from '@/stores/lookup'
import { adasStore } from '@/stores/adas'
import { dashboardStore } from '@/stores/dashboard'
import { opportunityStore } from '@/stores/opportunity'
import { fileStore } from '@/stores/file'
import { locationStore } from '@/stores/location'
import { adasIqStore } from '@/stores/adasIq'
import moment from 'moment'
import globals from '@/plugins/globals'
import ZSelect from '@/components/utilities/elements/Select.vue'
import ZTable from '@/components/utilities/elements/Table.vue'
import ZTabs from '@/components/utilities/elements/Tabs.vue'
import AppDocuments from '@/components/dashboard/elements/AppDocuments.vue'
import VehicleSummary from '@/components/dashboard/details/VehicleSummary.vue'
import AdasIqEmbed from '@/components/adas-iq/AdasIqEmbed.vue'
import Loader from '@/components/utilities/elements/Loader.vue'
import DialogPreview from '@/components/utilities/elements/Dialog.vue'
import DialogSchedule from '@/components/dashboard/forms/DialogSchedule.vue'
import RequestNotes from '@/components/dashboard/elements/RequestNotes.vue'
import DialogConfirm from '@/components/utilities/elements/ConfirmDialog.vue'
export default {
  name: 'VehicleDetails',
  components: {
    ZSelect,
    ZTable,
    ZTabs,
    AppDocuments,
    VehicleSummary,
    AdasIqEmbed,
    Loader,
    DialogPreview,
    DialogSchedule,
    RequestNotes,
    DialogConfirm
  },
  data() {
    return {
      tableHeader: [
        { field: 'category', label: 'Category', sortable: false },
        { field: 'location', label: 'Location', sortable: false },
        { field: 'system', label: 'System', sortable: false },
        { field: 'calibration', label: 'Calibration', sortable: false },
        { field: 'trigger', label: '', sortable: false, class: 'custom-column-60-center' },
        { field: 'completed', label: 'Completed', sortable: false }
      ],
      notRequiredHeader: [
        { field: 'category', label: 'Category', sortable: false },
        { field: 'location', label: 'Location', sortable: false },
        { field: 'system', label: 'System', sortable: false },
        { field: 'calibration', label: 'Calibration', sortable: false },
        { field: 'trigger', label: '', sortable: false, class: 'custom-column-60-center' }
      ],
      calibrationTab: 'Required',
      adasIqOpen: false,
      vin: null,
      adasInspection: null,
      triggerNotice: null,
      calibrationName: null,
      isLoading: false,
      cameFrom: null,
      mimeType: 'application/pdf',
      height: '100%',
      confirmTitle: 'Confirm',
      confirmAction: 'Proceed',
      confirmQuestion: 'Are you sure you wish to ?',
      actionConfirm: null,
      actionPreview: null,
      actionSchedule: null,
      previewContent: null,
      isError: false,
      errorMessage: null,
      isSave: false,
      opportunityId: null,
      newStatus: 0,
      // the status the provider picked, held until the confirm dialog resolves;
      // newStatus is bound to the select and can be reset by a re-render
      pendingStatus: 0,
      isText: false,
      defaultText: null,
      isChoice: false,
      choiceLabel: 'Reason:',
      choices: [],
      isDontLoad: false
    }
  },
  mounted() {
    adasIqStore().check()
    this.isDontLoad = false
    console.log('Route Params', this.$route.params)
    this.vin = this.$route.params.vin
    this.adasInspection = this.$route.params.adasinspection
    this.cameFrom = this.$route.params.camefrom
    if (this.$route.params.opportunity) {
      this.opportunityId = this.$route.params.opportunity
      this.emitter.on('process-opportunity', async (data) => {
        if (data) {
          if (this.opportunity && !this.isDontLoad) {
            if (
              data.source === this.opportunity.source &&
              (parseInt(data.id) === parseInt(this.opportunity.adasInspection) || parseInt(data.event) === parseInt(this.opportunity.opportunity))
            ) {
              await this.getAdasInspection({
                source: this.source,
                adasInspection: this.adasInspection,
                isInsurer: this.isInsurer,
                opportunity: this.opportunityId
              })
              // this.getRequestNotes({
              //   calibrateRequest: this.opportunity.calibrateRequest,
              //   opportunity: this.opportunity.opportunity,
              //   readBy: `${this.profile.firstName} ${this.profile.lastName}`
              // })
            }
          }
        }
      })
    }
    this.loadFromRoute()
  },
  watch: {
    // Same component instance when moving between opportunities, so reload on a param change.
    '$route.params.opportunity'(value, old) {
      if (value && value !== old) {
        this.vin = this.$route.params.vin
        this.adasInspection = this.$route.params.adasinspection
        this.opportunityId = value
        this.loadFromRoute()
      }
    }
  },
  computed: {
    ...mapState(authStore, ['dashboardName', 'profile']),
    ...mapState(lookupStore, ['lookups']),
    ...mapState(adasStore, ['inspectionInfo']),
    ...mapState(dashboardStore, ['firm', 'routeBack']),
    ...mapState(opportunityStore, ['opportunity']),
    ...mapState(fileStore, ['fileData']),
    // #620: ADAS IQ only for users ADAS IQ says may use it (stores/adasIq.js)
    ...mapState(adasIqStore, { hasAdasIq: 'allowed' }),
    routeTo() {
      if (this.cameFrom === 'vinsearch') {
        return '/dashboard/'
      }
      let _url = ''
      if (sessionStorage.getItem('dashboardName') === 'calibration') {
        _url = `/calibration/${this.firmId}`
      } else if (sessionStorage.getItem('dashboardName') === 'repairnetwork') {
        if (this.cameFrom === 'location') {
          _url = `/repairnetwork/${this.firmId}`
        } else {
          _url = `/dashboard/`
        }
      } else if (sessionStorage.getItem('dashboardName') === 'collision') {
        _url = `/dashboard/`
      }
      if (this.routeBack !== null) {
        _url = `/${this.routeBack}/`
      }
      return _url
    },
    isAdmin() {
      let _is = false
      if ([1, 2, 3, 5].includes(this.profile.roles[0])) {
        _is = true
      }
      return _is
    },
    isInsurer() {
      let _is = false
      if (this.profile.connectClass === 1) {
        _is = true
      }
      return _is
    },
    isMissingVehicleMake() {
      // only once the provider opportunity has loaded (adas.js sets vehicleMake alongside it)
      return !this.isInsurer && !!this.inspectionInfo?.opportunity && this.inspectionInfo.vehicleMake === null
    },
    isProvider() {
      let _is = false
      if ([2, 3].includes(this.profile.connectClass)) {
        _is = true
      }
      return _is
    },
    isRepairNetwork() {
      let _is = false
      if (this.dashboardName === 'repairnetwork') {
        _is = true
      }
      return _is
    },
    firmId() {
      let _data = null
      if (this.firm) {
        _data = this.firm.firm
      }
      return _data
    },
    firmName() {
      let _data = null
      if (this.firm) {
        _data = this.firm.name
      }
      return _data
    },
    source() {
      let _data = null
      if (this.firm) {
        _data = this.firm.source
      }
      return _data
    },
    // What the ADAS IQ panel pins: the ADAS Map inspection, the instance holding it, the VIN,
    // and a label for the chat list.
    adasIqContext() {
      if (!this.adasInspection) {
        return null
      }
      let _label = `Request #${this.adasInspection}`
      if (this.firmName) {
        _label = `${this.firmName} - ${_label}`
      }
      return {
        adasInspection: parseInt(this.adasInspection),
        source: this.source || null,
        vin: this.vin || null,
        label: _label
      }
    },
    providerName() {
      let _name = null
      if (this.inspectionInfo) {
        _name = this.inspectionInfo.providerName
      }
      console.log('Provider Name', {
        inspectionInfo: this.inspectionInfo,
        name: _name
      })
      return _name
    },
    inspectionDate() {
      let _data = null
      if (this.inspectionInfo) {
        _data = moment(this.inspectionInfo.inspectionDate).format('MM/DD/YYYY')
      }
      return _data
    },
    deliveryDate() {
      let _data = null
      if (this.inspectionInfo) {
        _data = moment(this.inspectionInfo.completedDate).format('MM/DD/YYYY')
      }
      return _data
    },
    calibrations() {
      let _data = []
      if (this.inspectionInfo && Array.isArray(this.inspectionInfo.results)) {
        _data = this.inspectionInfo.results
        // #675: raw clientInspection rows include Not Required / Not Equipped systems; opportunity lines are already priced work.
        if (!this.inspectionInfo.isOpportunityLines) {
          _data = _data.filter((r) => r.isRequired === true && !r.isNotEquipped)
        }
      }
      console.log('Calibrations', _data)
      return _data
    },
    notRequiredCalibrations() {
      let _data = []
      if (this.inspectionInfo && Array.isArray(this.inspectionInfo.identifiedResults)) {
        _data = this.inspectionInfo.identifiedResults.filter((r) => r.isRequired !== true || r.isNotEquipped)
      }
      return _data
    },
    calibrationTabs() {
      return ['Required', 'Not Required']
    },
    // #539: where the Amount $ column's pricing came from. Opportunity_Get reports
    // 'insurer' (IsPriceIncluded=1, the rate ADAS Map sent) or 'provider' (this
    // provider's own CalibrationPrice). Null until an opportunity has been loaded.
    pricingSourceBadge() {
      if (!this.inspectionInfo) return null
      const source = this.inspectionInfo.pricingSource
      if (!source) return null
      return { source, detail: this.inspectionInfo.pricingDetail }
    },
    tableColumnsName() {
      let columns = [
        {
          field: 'id',
          label: 'Id',
          sortable: false,
          class: 'custom-column-80 text-center'
        },
        {
          field: 'category',
          label: 'Category',
          sortable: false
        },
        {
          field: 'location',
          label: 'Location',
          sortable: false
        },
        {
          field: 'system',
          label: 'System',
          sortable: false
        },
        {
          field: 'calibration',
          label: 'Calibration',
          sortable: false
        },
        {
          field: 'amount',
          label: 'Amount $',
          sortable: false,
          class: 'custom-column-100 text-right'
        }
      ]
      if (this.isLocked) {
        columns.push({
          field: 'discount',
          label: 'Discount $',
          sortable: false,
          class: 'custom-column-100 text-right'
        })
      }
      if (!this.isLocked) {
        columns.push({
          field: 'id',
          label: 'Price $',
          sortable: false,
          class: 'custom-column-130 text-center'
        })
      }
      return columns
    },
    statuses() {
      let _data = []
      if (this.inspectionInfo) {
        // cancelled, voided, hold
        if (this.inspectionInfo.status === 5 || this.inspectionInfo.status === 6 || this.inspectionInfo.status === 10) {
          if (this.inspectionInfo.reasonSource === 'provider') {
            if (this.isAdmin && (this.statusName === 'Cancelled' || this.statusName === 'Completed')) {
              _data.push({
                id: 99,
                name: 'Re-open'
              })
            } else {
              _data.push({
                id: 8,
                name: 'Submitted OK'
              })
            }
          }
        }
      }
      if (_data.length === 0) {
        if (this.lookups['Status']) {
          if (this.isCompleted) {
            _data = [
              {
                id: 99,
                isInactive: false,
                key: 'Status',
                name: 'Re-open',
                sort: 0,
                value: 99
              }
            ]
          } else {
            // #582: Working (3) and Completed (4) are no longer gated on having a
            // technician assigned — a provider can progress/complete a request
            // whether or not they've used Confirm / Assign Technician.
            const ids = [3, 4, 5, 6, 8, 10]
            _data = this.lookups['Status'].filter((item) => ids.includes(item.id))
            _data = globals.sortArray(_data, 'name')
          }
        }
      }
      const addl = [{ id: 0, name: 'change status' }]
      _data = [...addl, ..._data]
      // No setDefaultStatus() here: this computed re-runs whenever the inspection
      // or the lookups refresh, and resetting newStatus from inside it wiped the
      // provider's pick while the confirm dialog was open. A late refresh between
      // picking "Completed" and clicking Complete then saved status 0 ("change
      // status"), which has no Status row, so both CCP and ADAS Map showed no
      // status at all. The select is reset where the action finishes instead.
      console.log('Statuses', _data)
      return _data
    },
    isReason() {
      let _is = false
      if (this.inspectionInfo) {
        if (this.inspectionInfo.reason) {
          _is = true
        }
      }
      return _is
    },
    reasonName() {
      let _name = null
      if (this.inspectionInfo) {
        if (this.inspectionInfo.reason) {
          console.log('Reason', this.inspectionInfo.reason)
          if (this.lookups['Reason']) {
            let found = this.lookups['Reason'].find((item) => parseInt(item.id) === parseInt(this.inspectionInfo.reason))
            if (found) {
              _name = found.name
            }
          }
        }
      }
      return _name
    },
    isLocked() {
      let _is = false
      if (this.inspectionInfo) {
        _is = this.inspectionInfo.isPriceIncluded
      }
      return _is
    },
    isCompleted() {
      let _is = false
      if (this.status) {
        // completed, archived
        if (this.status === 4 || this.status === 7) {
          _is = true
        }
      }
      return _is
    },
    isDisabled() {
      let _is = false
      if (this.inspectionInfo) {
        // cancelled, voided, hold
        if (this.inspectionInfo.status === 5 || this.inspectionInfo.status === 6 || this.inspectionInfo.status === 10) {
          if (this.inspectionInfo.reasonSource === 'shop') {
            _is = true
          }
        }
      }
      return _is
    },
    canSchedule() {
      let _can = false
      if (this.statusName === 'Submitted' || this.statusName === 'Submitted OK') {
        _can = true
      }
      return _can
    },
    isScheduled() {
      return true
    },
    providerFirm() {
      let _data = null
      if (this.inspectionInfo) {
        _data = this.inspectionInfo.providerFirm
      }
      return _data
    },
    scheduledDate() {
      let _data = null
      if (this.inspectionInfo) {
        if (this.inspectionInfo.scheduledOn) {
          let dateStr = moment(this.inspectionInfo.scheduledOn).local().format('MM/DD/YYYY')
          let timeOfDay = this.inspectionInfo.preferredTimeOfDay
          _data = timeOfDay ? `${dateStr} (${timeOfDay})` : dateStr
        }
      }
      return _data
    },
    techs() {
      let _data = ''
      if (this.inspectionInfo) {
        if (this.inspectionInfo.technicians) {
          this.inspectionInfo.technicians.forEach((element) => {
            if (_data !== '') {
              _data += ', '
            }
            _data += element.techName
          })
        }
      }
      return _data
    },
    status() {
      let _data = null
      if (this.inspectionInfo) {
        _data = this.inspectionInfo.status
      }
      return _data
    },
    statusName() {
      let _data = ''
      if (this.lookups['Status']) {
        let found = this.lookups['Status'].find((item) => parseInt(item.id) === parseInt(this.status))
        if (found) {
          _data = found.name
        }
      }
      return _data
    },
    statusClass() {
      let _class = ''
      let status = this.statusName
      if (status === 'Pending' || status === 'Hold') {
        _class = 'statusPending'
      } else if (status === 'Submitted') {
        _class = 'statusSubmitted'
      } else if (status === 'Working' || status === 'Submitted OK') {
        _class = 'statusWorking'
      } else if (status === 'Cancelled' || status === 'Voided') {
        _class = 'statusCancelled'
      } else if (status === 'Assigned') {
        _class = 'statusAssigned'
      } else if (status === 'Paid') {
        _class = 'statusPaid'
      } else {
        _class = 'statusCompleted'
      }
      return _class
    },
    isPending() {
      let _is = false
      if (this.status === 1) {
        _is = true
      }
      return _is
    },
    // Issue 622: the request came in as a PDF estimate this provider uploaded, rather than from a shop.
    isProviderUpload() {
      return !!(this.inspectionInfo && this.inspectionInfo.repairDocument)
    },
    requestNoteCount() {
      let _notes = []
      if (this.inspectionInfo) {
        if (this.inspectionInfo.notes) {
          _notes = this.inspectionInfo.notes.filter((note) => !note.isRemoved && note.noteType === 2)
        }
      }
      return _notes.length
    },
    scansCount() {
      let _data = 0
      if (this.fileData) {
        if (this.fileData['clientscans']) {
          _data = this.fileData['clientscans'].length
        }
      }
      return _data
    },
    docCount() {
      let _data = 0
      if (this.fileData) {
        if (this.fileData['clientfiles']) {
          _data = this.fileData['clientfiles'].length
        }
      }
      console.log('Doc Count', this.fileData)
      return _data
    }
  },
  methods: {
    ...mapActions(adasStore, ['getAdasInspection', 'inspectionReport', 'setInspection', 'inspectionInvoice']),
    ...mapActions(lookupStore, ['getLookups']),
    ...mapActions(opportunityStore, ['saveRequest', 'saveNotes', 'getRequestNotes', 'saveStatus', 'saveOpportunityLines', 'pushToAdasMap']),
    ...mapActions(fileStore, ['uploadFile']),
    ...mapActions(locationStore, ['getLocationDetails']),
    ...mapActions(opportunityStore, ['getOpportunity']),
    ...mapActions(dashboardStore, ['setFirm']),
    // Links, refresh and bookmarks arrive without a grid click having set the shop firm/source, so
    // take them from the opportunity itself before loading the inspection.
    async loadFromRoute() {
      if (this.opportunityId) {
        try {
          const opp = await this.getOpportunity({ opportunity: this.opportunityId, adasInspection: null })
          // Opportunity_Get returns the source firm id as "firmName".
          const sourceFirm = opp && opp.firmName != null ? String(opp.firmName) : null
          if (opp && opp.source && (opp.source !== this.source || sourceFirm !== String(this.firmId))) {
            this.setFirm({ firm: sourceFirm, name: '', source: opp.source })
          }
        } catch (e) {
          console.log('Error resolving opportunity firm', e)
        }
      }
      await this.init()
      if (this.firm && !this.firm.name && this.inspectionInfo && this.inspectionInfo.firmName) {
        this.setFirm({ ...this.firm, name: this.inspectionInfo.firmName })
      }
    },
    async init() {
      this.isLoading = true
      this.actionConfirm = null
      this.actionPreview = null
      this.actionSchedule = null
      this.newStatus = 0
      this.isSave = false
      console.log('Calling getAdasInspection...', {
        adasInspection: this.adasInspection,
        source: this.source,
        opportunity: this.opportunityId
      })
      await this.getAdasInspection({
        source: this.source,
        adasInspection: this.adasInspection,
        isInsurer: this.isInsurer,
        opportunity: this.opportunityId
      })
      console.log('Inspection Info', this.inspectionInfo)
      if (this.isProvider) {
        await this.getRequestNotes({
          calibrateRequest: this.inspectionInfo.calibrateRequest,
          opportunity: this.inspectionInfo.opportunity,
          readBy: `${this.profile.firstName} ${this.profile.lastName}`
        })
      }
      await this.getLookups({
        keys: ['Technician', 'Status', 'Reason'],
        firm: this.providerFirm,
        refresh: true
      })
      this.isLoading = false
    },
    setDefaultStatus() {
      this.newStatus = 0
    },
    formatDate(date, type = 'datetime') {
      return globals.formatDate(date, type)
    },
    formatNumber(value) {
      let _data = null
      if (value) {
        _data = globals.formatNumber(value)
      }
      if (_data === null) {
        _data = globals.formatNumber(0)
      }
      return _data
    },
    // #539: same wording as the ADAS Map Provider tab and the Blueprint badge.
    pricingSourceLabel(source) {
      switch (source) {
        case 'insurer': return 'Insurance'
        case 'provider': return 'Provider'
        default: return source
      }
    },
    closeModal() {
      this.actionConfirm = null
      this.actionPreview = null
      this.actionSchedule = null
      this.isError = false
      this.errorMessage = null
      this.newStatus = 0
      this.pendingStatus = 0
      this.confirmTitle = ''
      this.confirmAction = ''
      this.confirmQuestion = ''
      this.isText = false
      this.defaultText = null
      this.isChoice = false
      this.choices = []
    },
    prepTrigger(content) {
      let _data = null
      // console.log('Content', content)
      if (content) {
        let trigger = null
        if (content.toUpperCase().indexOf('<TRIGGER') >= 0) {
          trigger = content.slice(15, -16)
        } else {
          trigger = content
        }
        trigger = trigger.replace(/&comma;/g, ',')
        // console.log('Trigger', trigger)
        _data = `<div class='popover' role='tooltip' style='overflow-x: hidden !important;  overflow-y: auto !important;  max-height: 200px !important;'><div class='popover-body' >${trigger}</div></div>`
      }
      return _data
    },
    setOfferPrice(id, value) {
      this.setInspection({
        name: 'line',
        value: {
          id: id,
          offerPrice: parseFloat(value)
        }
      })
      this.isSave = true
    },
    base64ToBlob(base64) {
      const binaryString = atob(base64)
      const arrayBuffer = new ArrayBuffer(binaryString.length)
      const uint8Array = new Uint8Array(arrayBuffer)
      for (let i = 0; i < binaryString.length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i)
      }
      return new Blob([uint8Array], { type: this.mimeType })
    },
    async report() {
      this.mimeType = 'application/pdf'
      this.height = '650px'
      this.previewContent = null
      this.isLoadingReport = true
      let results = await this.inspectionReport(this.source, this.adasInspection)
      if (results) {
        const blob = this.base64ToBlob(results)
        this.previewContent = URL.createObjectURL(blob)
        this.actionPreview = true
      }
      this.isLoadingReport = false
    },
    async invoice() {
      this.mimeType = 'application/pdf'
      this.height = '650px'
      this.previewContent = null
      this.isLoadingReport = true
      let provider = null
      let details = await this.getLocationDetails(this.inspectionInfo.providerFirm)
      if (details) {
        provider = {
          locationName: details.firmName,
          locationAddressLines: `${details.addressLines}`,
          locationCity: details.city,
          locationStateProvince: details.stateProvince,
          locationPostalCode: details.postalCode,
          locationPhoneNumber: details.phoneNo,
          locationWebAddress: details.webAddress
        }
      }
      console.log('Inspection Items', {
        providerFirm: this.inspectionInfo.providerFirm,
        provider: provider,
        results: this.inspectionInfo.results
      })
      let results = await this.inspectionInvoice(this.source, this.adasInspection, this.calibrations, provider)
      if (results) {
        const blob = this.base64ToBlob(results)
        this.previewContent = URL.createObjectURL(blob)
        this.actionPreview = true
      }
      this.isLoadingReport = false
    },
    confirm() {
      this.actionSchedule = true
    },
    async saveRequestNotes() {
      console.log('Save Notes', this.inspectionInfo)
      await this.saveNotes({
        calibrateRequest: this.inspectionInfo.calibrateRequest,
        opportunity: this.inspectionInfo.opportunity,
        notes: this.inspectionInfo.notes
      })
    },
    async assignedTech() {
      console.log('Assigned Tech', this.selectedSlot)
    },
    async savedSchedule(slot) {
      this.actionSchedule = null
      console.log('Saved Schedule', this.selectedSlot)
      await this.getAdasInspection({
        source: this.source,
        adasInspection: this.adasInspection,
        isInsurer: this.isInsurer,
        opportunity: this.opportunityId
      })
    },
    async saveOffer() {
      this.isSave = false
      let data = {
        opportunity: this.inspectionInfo.opportunity,
        lines: []
      }
      this.inspectionInfo.results.forEach((element) => {
        data.lines.push({
          calibrateRequestItem: parseInt(element.calibrateRequestItem),
          price: element.offerPrice !== null ? parseFloat(element.offerPrice) : 0
        })
      })
      console.log('saveOffer', {
        inspection: this.inspectionInfo,
        data: data
      })
      await this.saveOpportunityLines(data)
    },
    async sendOffer() {
      this.pushToAdasMap(this.inspectionInfo.opportunity)
    },
    async updateStatus() {
      // reset
      this.actionConfirm = null
      this.isText = false
      this.defaultText = null
      this.isChoice = false
      this.choices = []
      if (this.newStatus === 0) {
        return
      }
      this.isDontLoad = true
      // hold the pick: the select's own value can be reset by a re-render before
      // the provider confirms
      const chosen = parseInt(this.newStatus)
      this.pendingStatus = chosen
      // get the action
      let action = this.statuses.find((item) => parseInt(item.id) === chosen).name
      switch (action) {
        case 'Completed':
          this.confirmTitle = 'Complete Request'
          this.confirmAction = 'Complete'
          this.confirmQuestion = 'Are you sure you wish to Complete this Request?'
          break
        case 'Cancelled':
          this.confirmTitle = 'Cancel Request'
          this.confirmAction = 'Proceed'
          this.confirmQuestion = 'Are you sure you wish to Cancel this Request?'
          this.isText = true
          this.isChoice = true
          this.choices = this.lookups['Reason'].filter((item) => parseInt(item.code) === 1)
          break
        case 'Voided':
          this.confirmTitle = 'Void Request'
          this.confirmAction = 'Void'
          this.confirmQuestion = 'Are you sure you wish to Void this Request?'
          this.isText = true
          this.isChoice = true
          this.choices = this.lookups['Reason'].filter((item) => parseInt(item.code) === 3)
          break
        case 'Hold':
          this.confirmTitle = 'Hold Request'
          this.confirmAction = 'Hold'
          this.confirmQuestion = 'Are you sure you wish to put this Request on Hold?'
          this.isText = true
          this.isChoice = true
          this.choices = this.lookups['Reason'].filter((item) => parseInt(item.code) === 2)
          break
        case 'Re-open':
          this.confirmTitle = 'Re-open Request'
          this.confirmAction = 'Re-open'
          this.confirmQuestion = 'Are you sure you wish to Re-open this Request?'
          break
        default:
          this.confirmTitle = ''
          this.confirmAction = ''
          this.confirmQuestion = ''
          break
      }
      if (this.confirmTitle !== '') {
        this.actionConfirm = true
      } else {
        // no confirmation, just update the status
        console.log('Update Status', action)
        this.setInspection({
          name: 'status',
          value: chosen
        })
        this.setInspection({
          name: 'reason',
          value: null
        })
        this.newStatus = 0
        // save status
        let data = {
          opportunity: this.inspectionInfo.opportunity,
          status: this.inspectionInfo.status,
          statusName: this.statuses.find((item) => parseInt(item.id) === parseInt(this.inspectionInfo.status)).name
        }
        await this.saveStatus(data)
        // get the notes
        await this.getRequestNotes({
          calibrateRequest: this.inspectionInfo.calibrateRequest,
          opportunity: this.inspectionInfo.opportunity,
          readBy: `${this.profile.firstName} ${this.profile.lastName}`
        })
        // clean up
        this.newStatus = 0
        this.confirmTitle = ''
        this.confirmAction = ''
        this.confirmQuestion = ''
        this.isText = false
        this.defaultText = null
        this.isChoice = false
        this.choices = []
      }
    },
    async performConfirmation(confirmationData) {
      this.actionConfirm = false
      let data = null
      // use the status held at pick time, never the select's current value
      let status = this.pendingStatus
      if (!(status > 0)) {
        // nothing valid was picked (or it was lost): never save "change status"
        console.log('Status confirm ignored: no pending status', { newStatus: this.newStatus })
        this.closeModal()
        return
      }
      // re-open?
      if (status === 99) {
        status = 8
        data = {
          opportunity: this.inspectionInfo.opportunity,
          status: 8,
          statusName: 'Submitted OK'
        }
      } else {
        // otherwise, normal
        data = {
          opportunity: this.inspectionInfo.opportunity,
          status: status,
          statusName: this.statuses.find((item) => parseInt(item.id) === status).name
        }
      }
      // save the status to the opportunity locally
      this.setInspection({
        name: 'status',
        value: status
      })
      if (this.confirmAction === 'Proceed' || this.confirmAction === 'Void' || this.confirmAction === 'Hold') {
        // any action that requires a reason
        this.setInspection({
          name: 'reason',
          value: parseInt(confirmationData.choice)
        })
        this.setInspection({
          name: 'reasonSource',
          value: 'provider'
        })
        data.createdBy = `${this.profile.firstName} ${this.profile.lastName}`
        data.reason = {
          reason: confirmationData.choice,
          notes: confirmationData.confirmText,
          source: 'provider'
        }
      } else {
        this.setInspection({
          name: 'reason',
          value: null
        })
      }
      // save the status to the backend
      await this.saveStatus(data)
      // get the notes
      await this.getRequestNotes({
        calibrateRequest: this.inspectionInfo.calibrateRequest,
        opportunity: this.inspectionInfo.opportunity,
        readBy: `${this.profile.firstName} ${this.profile.lastName}`
      })
      console.log('Adas Inspection (after Save Status/Get Request Notes)', this.inspectionInfo)
      // if the status is Complete, trigger the production of the invoice
      if (this.confirmAction === 'Complete') {
        if (this.inspectionInfo !== undefined && this.inspectionInfo !== null) {
          let provider = null
          let details = await this.getLocationDetails(this.inspectionInfo.providerFirm)
          if (details) {
            provider = {
              locationName: details.firmName,
              locationAddressLines: `${details.addressLines}`,
              locationCity: details.city,
              locationStateProvince: details.stateProvince,
              locationPostalCode: details.postalCode,
              locationPhoneNumber: details.phoneNo,
              locationWebAddress: details.webAddress
            }
          }
          console.log('Inspection Items', {
            providerFirm: this.inspectionInfo.providerFirm,
            provider: provider,
            results: this.inspectionInfo.results
          })
          let results = await this.inspectionInvoice(this.source, this.adasInspection, this.calibrations, provider)
          // let results = await this.inspectionInvoice(this.source, this.adasInspection, this.inspectionInfo.results)
          if (results) {
            // upload the invoice to the documents
            const blob = this.base64ToBlob(results)
            await this.uploadFile({
              blob: blob,
              folder: 'clientfiles',
              filename: `invoice_${this.inspectionInfo.adasInspection}.pdf`,
              firm: this.firmId,
              vin: this.inspectionInfo.vehicle.vin,
              id: this.opportunityId,
            })            
            await this.getAdasInspection({
              source: this.source,
              adasInspection: this.adasInspection,
              isInsurer: this.isInsurer,
              opportunity: this.opportunityId
            })
            this.isDontLoad = false
          }
        }
      }
      // clean up
      this.isDontLoad = false
      this.newStatus = 0
      this.pendingStatus = 0
      this.confirmTitle = ''
      this.confirmAction = ''
      this.confirmQuestion = ''
      this.isText = false
      this.defaultText = null
      this.isChoice = false
      this.choices = []
    },
    viewReason() {
      // view the reason (if any)
      if (this.inspectionInfo) {
        if (this.inspectionInfo.notes) {
          let notes = this.inspectionInfo.notes.filter((note) => parseInt(note.reason) === parseInt(this.inspectionInfo.reason))
          if (notes.length > 0) {
            if (notes[0].note !== null) {
              console.log('View Reason', {
                inspection: this.inspectionInfo,
                notes: notes[0].note
              })
              this.confirmTitle = 'Reason Notes'
              this.confirmAction = ''
              this.confirmQuestion = `author: ${notes[0].createdBy}`
              this.isText = true
              this.defaultText = notes[0].note
              this.actionConfirm = true
            }
          }
        }
      }
    },
    submitIssue() {
      let userProfile = this.profile
      // https://form.jotform.com/232344425605047?multipleshopOperators=[SHOPNAME]&individualsFull=[FULLNAME]&email=[EMAIL]
      // &vehicleIdentification=[VIN]&vehicleYear=[YEAR]&vehicleMake=[MAKE]&model=[MODEL]&adasMap=[INSPECTION_ID]
      // https://form.jotform.com/242326001332136?multipleshopOperators=CRESTWOOD%20-%20CC&IndividualsFullName=Nelson%20Vargas&Email=nelson.vargas@opusivs.com&vehicleIdentification=JTMZFREV6HJ708211&vehicleYear=2017&vehicleMake=Toyota&model=RAV4%20LE%20FWD&adasMap=784761
      let baseUrl = 'https://form.jotform.com/242326001332136'
      if (this.isProvider) {
        baseUrl = 'https://form.jotform.com/250406664278158'
      }
      let url = `${baseUrl}?multipleshopOperators=[SHOPNAME]&IndividualsFullName=[FULLNAME]&Email=[EMAIL]&vehicleIdentification=[VIN]&vehicleYear=[YEAR]&vehicleMake=[MAKE]&model=[MODEL]&adasMap=[INSPECTION_ID]`
      url = url
        .replace('[SHOPNAME]', `${this.inspectionInfo.firmName}`)
        .replace('[FULLNAME]', `${userProfile.firstName} ${userProfile.lastName}`)
        .replace('[EMAIL]', userProfile.username)
      if (this.inspectionInfo) {
        url = url.replace('[INSPECTION_ID]', this.inspectionInfo.adasInspection)
        if (this.inspectionInfo.vehicle) {
          url = url.replace('[VIN]', this.inspectionInfo.vehicle.vin)
          url = url.replace('[YEAR]', this.inspectionInfo.vehicle.year)
          url = url.replace('[MAKE]', this.inspectionInfo.vehicle.make)
          url = url.replace('[MODEL]', this.inspectionInfo.vehicle.model)
        } else {
          url = url.replace('[VIN]', '')
          url = url.replace('[YEAR]', '')
          url = url.replace('[MAKE]', '')
          url = url.replace('[MODEL]', '')
        }
      } else {
        url = url.replace('[INSPECTION_ID]', '')
        url = url.replace('[VIN]', '')
        url = url.replace('[YEAR]', '')
        url = url.replace('[MAKE]', '')
        url = url.replace('[MODEL]', '')
      }
      window.open(url, '_blank')
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-arrow {
  font-size: 1.2em;
  font-weight: 500;
}

.custom-title {
  font-size: 1.2em;
  font-weight: 500;
}

.custom-status {
  position: relative;
}

.custom-select {
  width: 180px;
  right: 0;
}

.custom-recommend {
  display: inline-flex;
}

.custom-delivery {
  display: inline-flex;
  float: right;
}

.shop-list-container:nth-child(even) {
  background-color: #fcfcfd;
}

.shop-list-div:hover {
  background-color: rgb(97 169 36 / 10%);
  cursor: pointer;
}

.shop-list-div {
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 10px;
}

.shop-header-mobile {
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.dashboard-shop-card {
  max-height: 560px;
  overflow: auto;
}

.table-holder {
  border: 1px solid #e8eaf1;
  border-radius: 5px;
}

.custom-label {
  font-weight: 600;
}

.custom-numbers {
  font-weight: 600;
  color: blue;
}

.custom-textarea {
  height: 100px;
  width: 100%;
  margin-top: 15px;
  margin-bottom: 10px;
}

.custom-accordion-docs {
  display: inline-flex;
  width: 100%;
  flex-wrap: nowrap;
}

.custom-accordion-button {
  font-weight: 600;
  font-size: 1.1em;
}

.custom-upload {
  text-align: center;
  width: 100%;
}

.custom-note-count {
  margin-left: 25px;
  font-size: 0.8em;
}

.custom-reason {
  font-size: 0.8em;
  font-style: italic;
}

.statusPending {
  font-weight: 500;
  color: red;
}

.statusSubmitted {
  font-weight: 500;
  color: blue;
}

.statusWorking {
  font-weight: 500;
  color: green;
}

.statusCompleted {
  font-weight: 500;
  color: black;
}

.statusCancelled {
  font-weight: 500;
  color: rgb(116, 0, 0);
}

.statusAssigned {
  font-weight: 500;
  color: rgb(0, 121, 190);
}

.statusPaid {
  font-weight: 600;
  color: green;
}

.custom-service {
  position: relative;
}

.custom-discount {
  position: absolute;
  font-size: 0.8em;
  font-style: italic;
  color: red;
  right: 20px;
  bottom: -15px;
}

/* #539: Pricing Source badge, sits just above the calibrations table. */
.custom-pricing-source {
  font-size: 0.85em;
}

.custom-pricing-source-text {
  color: #6c757d;
  margin-right: 4px;
}

.custom-not-scheduled {
  color: #dc3545;
  font-weight: 600;
}
.custom-scheduled-highlight {
  border: 2px solid red;
  border-radius: 4px;
  padding: 8px 16px;
  display: inline-block;
  font-size: 1.1rem;
}
</style>
